import React from "react";
import { Check, Calendar, UserX, Gift, AlertCircle } from "lucide-react";
import { ScheduleV2 } from "../../../types/model/schedule";
import { Timeoff, ScheduleTimeoff, ScheduleV2Timeoff } from "../../../types/model/timeoff";
import { Unavailability } from "../../../types/model/unavailability";
import { ScheduleV2Holiday } from "../../../types/model/holiday";
import { DateTimeUtil } from "../../../utils";
import Avatar from "../../common/ngwAvatar";

type EventData = ScheduleV2 | ScheduleV2Timeoff | Unavailability | ScheduleV2Holiday;

interface GenericEventCardProps {
    data: EventData;
    dataType: "schedule" | "scheduleTimeoff" | "unavailability" | "holiday";
    includeTeam?: boolean;
    onClick?: (data: EventData) => void;
    className?: string;
}

const GenericEventCard: React.FC<GenericEventCardProps> = ({
    data,
    dataType,
    includeTeam = false,
    onClick,
    className = "",
}) => {
    const isClickable = dataType === "schedule" && onClick;

    // Helper function to get employee details based on data type
    const getEmployeeDetails = () => {
        switch (dataType) {
            case "schedule":
                const schedule = data as ScheduleV2;
                return {
                    name: schedule.employee
                        ? `${schedule.employee.first_name || ""} ${schedule.employee.last_name || ""}`.trim()
                        : null,
                    imageUrl: schedule.employee?.image_url,
                    id: schedule.employee?.emp_id,
                };
            // case 'timeoff':
            //     const timeoff = data as Timeoff;
            //     return {
            //         name: null, // Timeoff doesn't have employee_name in the current type
            //         imageUrl: null, // Timeoff doesn't have image_url in the current type
            //         id: timeoff.os_emp_account_id
            //     };
            case "scheduleTimeoff":
                const scheduleTimeoff = data as ScheduleV2Timeoff;
                return {
                    name:
                        scheduleTimeoff.employee?.first_name && scheduleTimeoff.employee?.last_name
                            ? `${scheduleTimeoff.employee.first_name} ${scheduleTimeoff.employee.last_name}`.trim()
                            : null,
                    imageUrl: scheduleTimeoff.employee?.image_url,
                    id: scheduleTimeoff.schedules_id,
                };
            case "unavailability":
                const unavailability = data as Unavailability;
                return {
                    name:
                        unavailability.employee?.first_name && unavailability.employee?.last_name
                            ? `${unavailability.employee.first_name} ${unavailability.employee.last_name}`.trim()
                            : unavailability.employee_name || null,
                    imageUrl: unavailability.employee?.image_url,
                    id: unavailability.employee?.employee_id || unavailability.employee_id,
                };
            case "holiday":
                const holiday = data as ScheduleV2Holiday;
                return {
                    name: null, // Holidays don't have employee names
                    imageUrl: null, // Holidays don't have employee images
                    id: holiday.schedules_id || null,
                };
            default:
                return { name: null, imageUrl: null, id: null };
        }
    };

    // Helper function to get event details based on data type
    const getEventDetails = () => {
        switch (dataType) {
            case "schedule":
                const schedule = data as ScheduleV2;
                return {
                    title: schedule.shift?.shift_name || "Untitled Event",
                    timeRange: DateTimeUtil.formatElegantTimeRange(schedule?.start_time, schedule?.end_time),
                    color: schedule.shift_color || "#3B82F6", // Use shift_color if available, otherwise default blue
                };
            case "scheduleTimeoff":
                const scheduleTimeoff = data as ScheduleV2Timeoff;
                const timeoffTitle = scheduleTimeoff.earnings_code_name || scheduleTimeoff.policy_name || "Time Off";
                const timeoffDetails = [];

                // Check if we should show time range based on show_time flag
                const shouldShowTime = scheduleTimeoff.show_time === true;
                
                if (shouldShowTime && scheduleTimeoff.start_datetime && scheduleTimeoff.end_datetime) {
                    // Extract time from datetime strings (format: "YYYY-MM-DD HH:mm:ss")
                    const startTime = scheduleTimeoff.start_datetime.split(" ")[1]?.substring(0, 5) || "";
                    const endTime = scheduleTimeoff.end_datetime.split(" ")[1]?.substring(0, 5) || "";
                    if (startTime && endTime) {
                        timeoffDetails.push(`${startTime}-${endTime}`);
                    }
                }

                // Always show hours if available
                if (scheduleTimeoff.hours) {
                    timeoffDetails.push(`${scheduleTimeoff.hours}h`);
                }

                return {
                    title: timeoffTitle,
                    timeRange: timeoffDetails.join(" • "),
                    color: "#10B981", // Green for scheduleTimeoff
                };
            case "unavailability":
                const unavailability = data as Unavailability;
                return {
                    title: "Unavailable",
                    timeRange: (() => {
                        if (unavailability.all_day === "1") {
                            return "All Day";
                        }

                        // Check for time fields (both possible field names)
                        const startTime = unavailability.start_time || unavailability.from_time;
                        const endTime = unavailability.end_time || unavailability.to_time;

                        if (unavailability.start_date && unavailability.end_date && startTime && endTime) {
                            const start = `${unavailability.start_date} ${startTime}`;
                            const end = `${unavailability.end_date} ${endTime}`;
                            return DateTimeUtil.formatDateTimeRangeUtc(start, end);
                        }

                        return null;
                    })(),
                    color: "#6B7280", // Gray for unavailability
                };
            case "holiday":
                const holiday = data as ScheduleV2Holiday;
                return {
                    title: holiday.title || "Holiday",
                    timeRange: (() => {
                        if (holiday.all_day === 1) {
                            return "All Day";
                        }

                        if (holiday.from_time && holiday.to_time) {
                            const formattedStartTime = DateTimeUtil.formatElegantTime(holiday.from_time);
                            const formattedEndTime = DateTimeUtil.formatElegantTime(holiday.to_time);
                            return `${formattedStartTime} - ${formattedEndTime}`;
                        }

                        if (holiday.start_date && holiday.end_date) {
                            const startDate = new Date(holiday.start_date);
                            const endDate = new Date(holiday.end_date);
                            if (startDate.getTime() === endDate.getTime()) {
                                return startDate.toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "2-digit",
                                    year: "numeric",
                                });
                            } else {
                                return DateTimeUtil.formatDateRangeFancy(holiday.start_date, holiday.end_date);
                            }
                        }

                        return null;
                    })(),
                    color: "#DC2626", // Red for holiday
                };
            default:
                return { title: "Unknown Event", timeRange: "", color: "#9CA3AF" };
        }
    };

    const employeeDetails = getEmployeeDetails();
    const eventDetails = getEventDetails();

    // Helper function to get the appropriate icon for each event type
    const getEventIcon = () => {
        switch (dataType) {
            case "schedule":
                return <Check className="w-4 h-4 flex-shrink-0" style={{ color: eventDetails.color }} />;
            case "scheduleTimeoff":
                return <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: eventDetails.color }} />;
            case "unavailability":
                return <UserX className="w-4 h-4 flex-shrink-0" style={{ color: eventDetails.color }} />;
            case "holiday":
                return <Gift className="w-4 h-4 flex-shrink-0" style={{ color: eventDetails.color }} />;
            default:
                return <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: eventDetails.color }} />;
        }
    };

    const handleClick = () => {
        if (isClickable && onClick) {
            onClick(data);
        }
    };

    return (
        <div
            className={`flex items-start w-full p-3 rounded-lg border border-gray-200 shadow-sm transition relative ${className}`}
            onClick={handleClick}
            style={{ 
                cursor: isClickable ? "pointer" : "default",
                backgroundColor: `${eventDetails.color}08` // Add subtle background tinting (8% opacity)
            }}
        >
            {/* Vertical Band on Left Border - Thicker with rounded corners */}
            <div
                className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-lg"
                style={{ backgroundColor: eventDetails.color }}
            />

            {/* Profile Picture or Holiday Icon */}
            <div className="flex-shrink-0 mr-3 ml-1">
                {dataType === "holiday" ? (
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center border-2 border-red-200">
                        <Gift className="w-6 h-6 text-red-600" />
                    </div>
                ) : (
                    <Avatar
                        src={employeeDetails.imageUrl}
                        name={employeeDetails.name || "Unknown Employee"}
                        className="w-12 h-12"
                    />
                )}
            </div>

            {/* Content Section */}
            <div className={`flex-1 ${!includeTeam || !employeeDetails.name ? "flex items-center" : ""}`}>
                {/* Employee Name - Only show when includeTeam is true and not a holiday */}
                {includeTeam && employeeDetails.name && dataType !== "holiday" && (
                    <h3 className="font-bold text-gray-900 text-base mb-1">{employeeDetails.name}</h3>
                )}

                {/* Event Details with Relevant Icon */}
                <div className="flex items-center gap-2">
                    {dataType !== "holiday" && getEventIcon()}
                    <span className="text-gray-600 text-sm">
                        {eventDetails.title} {eventDetails.timeRange}
                        {/* Show asterisk for schedules with swap/release in progress */}
                        {dataType === "schedule" && Number((data as ScheduleV2).os_schedule_shift_status) > 0 && (
                            <span className="relative group ml-1">
                                <span className="text-red-500 font-bold cursor-help">*</span>
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                    swap or release in progress
                                    {/* Tooltip arrow */}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                                </div>
                            </span>
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default GenericEventCard;
